/**
 * Used to render one dropdown element for fill in the blank questions
 * The prompt 'The sky is [blue*, red, green] and is filled with [water, air*, sand].'
 * would render TWO of these components, one for each blank

 * Example of fully rendered element (without all the HTML attributes):
 * <div className="dropdown inline">
 *   <button>Choose an answer</button>
 *   <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
 *     <a className='dropdown-item'>blue</a>
 *     <a className='dropdown-item'>red</a>
 *     <a className='dropdown-item'>green</a>
 *   </div>
 * </div>
 */

import React, { useMemo, useState } from 'react';
import I18n from 'modules/i18n';
import cn from '@/utils/cn';
import ScoreIcon from '@/components/common/Forms/ScoreIcon';
import styles from './Dropdown.module.scss';
import { fisherYatesShuffle } from '../../../../modules/TCIUtils';

interface DropdownProps {
  bracketedText: string;
  disabled?: boolean;
  handleAnswerSelect?: (index: number) => any;
  isComplete?: boolean;
  isPartiallyCorrect?: boolean;
  randomizeChoices?: boolean;
  randomizeSeed?: number;
  selectedAnswerIndex?: number;
  validateDropdownDisabled?: (choices: string[], index: number | undefined) => any;
}

const Dropdown = ({
  bracketedText,
  disabled = false,
  handleAnswerSelect = () => { },
  isComplete = false,
  isPartiallyCorrect = false,
  randomizeChoices = false,
  randomizeSeed,
  selectedAnswerIndex: initialSelectedAnswerIndex,
  validateDropdownDisabled = () => { },
}: DropdownProps) => {
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState(initialSelectedAnswerIndex);

  const parsedChoices = useMemo(() => (
    bracketedText
      .replace('[', '')
      .replace(']', '')
      .replace(/\*/g, '')
      .split(',')
      .map(choice => choice.trim())
  ), [bracketedText]);

  const answerChoices = useMemo(() => {
    if (randomizeChoices) return fisherYatesShuffle([...parsedChoices], randomizeSeed);

    return parsedChoices;
  }, [randomizeChoices]);

  const selectAnswerHandler = (index: number) => {
    const realIndex = randomizeChoices ? parsedChoices.findIndex(opt => opt === answerChoices[index]) : index;

    handleAnswerSelect(realIndex);
    setSelectedAnswerIndex(realIndex);
  };

  const handleKeyDown = (e, index) => {
    if ([' ', 'Enter'].includes(e.key)) {
      e.preventDefault();
      selectAnswerHandler(index);

      const dropButton = e.currentTarget.closest('.dropdown').querySelector('button');

      $(dropButton).trigger('click');
      dropButton.focus();
    }

    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      const currentElement = e.currentTarget as HTMLElement;
      const dropdownContent = currentElement.parentElement;

      if (dropdownContent) {
        const focusableItems = Array.from(
          dropdownContent.querySelectorAll('.dropdown-item'),
        ) as HTMLElement[];

        let nextIndex: number;
        if (['ArrowDown', 'ArrowRight'].includes(e.key)) {
          nextIndex = (index + 1) % answerChoices.length;
        } else {
          nextIndex = (index === 0) ? answerChoices.length - 1 : index - 1;
        }
        focusableItems[nextIndex]?.focus();
        setSelectedAnswerIndex(nextIndex);
      }
    }
  };

  const answer = parsedChoices[selectedAnswerIndex];
  const displayLabel = answer || I18n.t('choose_an_answer');

  const isCorrectAnswer = validateDropdownDisabled(answerChoices[selectedAnswerIndex], selectedAnswerIndex);

  return (
    <div className="dropdown inline">
      {(isComplete || isCorrectAnswer) && (
        <ScoreIcon
          isCorrect={isCorrectAnswer && !isPartiallyCorrect}
          isPartiallyCorrect={isPartiallyCorrect}
        />
      )}
      <button
        aria-expanded="false"
        aria-haspopup="listbox"
        className={cn(
          `dropdown-toggle btn btn--outline-purple ${styles.button}`, {
            [styles.correct]: isComplete && isCorrectAnswer,
            [styles.incorrect]: isComplete && !isCorrectAnswer,
          },
        )}
        dangerouslySetInnerHTML={{ __html: displayLabel }}
        data-toggle="dropdown"
        disabled={disabled || isCorrectAnswer}
        id="dropdownMenuButton"
        type="button"
      />

      <div aria-labelledby="dropdownMenuButton" className={`dropdown-menu ${styles.dropdownLink}`}>
        {answerChoices.map((choiceText: string, index: number) => (
          <a
            key={`${choiceText}-${index}`}
            aria-selected={selectedAnswerIndex === index}
            className={`dropdown-item ${styles.dropdownLink}`}
            dangerouslySetInnerHTML={{ __html: choiceText }}
            href="#"
            role="option"
            onClick={(e) => {
              e.preventDefault();

              selectAnswerHandler(index);
              const dropButton = e.currentTarget.closest('.dropdown').querySelector('button');
              dropButton.focus();
            }}
            onKeyDown={(e) => { handleKeyDown(e, index); }}
          />
        ))}
      </div>
    </div>
  );
};

export default Dropdown;
